import type { MutableRefObject } from "react"

const WEBM_MIME_TYPE = "audio/webm"
const WEBM_OPUS_MIME_TYPE = `${WEBM_MIME_TYPE};codecs=opus`
const OGG_MIME_TYPE = "audio/ogg"
const OGG_OPUS_MIME_TYPE = `${OGG_MIME_TYPE};codecs=opus`
const MP4_MIME_TYPE = "audio/mp4"

const DEFAULT_MIME_TYPE_CANDIDATES = [WEBM_OPUS_MIME_TYPE, WEBM_MIME_TYPE, OGG_OPUS_MIME_TYPE, MP4_MIME_TYPE]
const WEBKIT_MIME_TYPE_CANDIDATES = [MP4_MIME_TYPE, WEBM_OPUS_MIME_TYPE, WEBM_MIME_TYPE, OGG_OPUS_MIME_TYPE]

/**
 * Select the best supported audio MIME type for MediaRecorder.
 * WebKit prefers MP4 because Safari-produced WebM can be rejected by transcription providers.
 */
export function pickAudioMimeType(
  userAgent = typeof navigator === "undefined" ? "" : navigator.userAgent,
  isTypeSupported = (mimeType: string) =>
    typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(mimeType)
): string {
  const candidates = prefersMp4Recording(userAgent) ? WEBKIT_MIME_TYPE_CANDIDATES : DEFAULT_MIME_TYPE_CANDIDATES

  for (const mimeType of candidates) {
    if (isTypeSupported(mimeType)) {
      return mimeType
    }
  }

  return ""
}

/**
 * Stop a MediaRecorder and return the recorded audio as a Blob.
 * Resolves when the recorder emits 'stop', rejects on error.
 */
export function stopRecorderToBlob(recorder: MediaRecorder, chunksRef: MutableRefObject<BlobPart[]>): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const mimeType = recorder.mimeType

    const onStop = () => {
      recorder.removeEventListener("stop", onStop)
      recorder.removeEventListener("error", onError)
      resolve(new Blob(chunksRef.current, { type: mimeType || "application/octet-stream" }))
    }

    const onError = (e: Event) => {
      recorder.removeEventListener("stop", onStop)
      recorder.removeEventListener("error", onError)
      reject(e)
    }

    recorder.addEventListener("stop", onStop, { once: true })
    recorder.addEventListener("error", onError, { once: true })

    recorder.stop()
  })
}

/**
 * Transcribe audio using the Deepgram API route.
 * @param blob - Audio blob to transcribe
 * @param keywords - Optional array of keywords to improve recognition (names, terms, etc.)
 */
export async function transcribeWithDeepgram(blob: Blob, keywords?: string[]): Promise<string> {
  const formData = new FormData()
  formData.append("audio", blob, audioFilename(blob.type))

  if (keywords?.length) {
    formData.append("keywords", JSON.stringify(keywords))
  }

  const response = await fetch("/api/deepgram/transcribe", {
    method: "POST",
    body: formData,
  })

  const json = await response.json().catch(() => ({}))

  if (!response.ok) {
    throw new Error(extractErrorMessage(json, response.status))
  }

  return (json?.transcript as string) || ""
}

export function audioFilename(mimeType: string): string {
  const normalizedMimeType = mimeType.split(";", 1)[0].toLowerCase()

  switch (normalizedMimeType) {
    case MP4_MIME_TYPE:
      return "dictation.mp4"
    case OGG_MIME_TYPE:
      return "dictation.ogg"
    case WEBM_MIME_TYPE:
      return "dictation.webm"
    default:
      return "dictation.bin"
  }
}

function prefersMp4Recording(userAgent: string): boolean {
  const usesAppleWebKit = /\bAppleWebKit\//.test(userAgent)
  const usesDesktopChromium = /\b(?:Chrome|Chromium|Edg|OPR)\//.test(userAgent)

  return usesAppleWebKit && !usesDesktopChromium
}

function extractErrorMessage(json: unknown, status: number): string {
  const fallback = `Transcription failed (${status})`
  if (!json || typeof json !== "object") return fallback

  const { error } = json as { error?: unknown }
  if (typeof error === "string" && error.trim()) return error
  if (error && typeof error === "object") {
    const body = error as Record<string, unknown>
    const candidates = [body.message, body.err_msg, body.reason]
    for (const candidate of candidates) {
      if (typeof candidate === "string" && candidate.trim()) return candidate
    }
  }
  return fallback
}
